import ActivityLogSidebar from "../../components/sidebars/ActivityLogSidebar"
import ParticipantGroupSidebar from "../../components/sidebars/ParticipantGroupSidebar"
import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import StaffGroupSidebar from "../../components/sidebars/StaffGroupSidebar"
import StaffSidebar from "../../components/sidebars/StaffSidebar"

function hasValue(value) {
  return value !== null && value !== undefined && value !== ""
}

// Determines if the given template ID corresponds to an activity template.
function isActivityTemplateId(idTemplate) {
  if (!hasValue(idTemplate)) {
    return false
  }

  // Legacy IDs that should always resolve to the activity sidebar.
  const explicitActivityTemplateIds = [
    "contact_log",
    "activity_employer_contact",
  ]

  if (explicitActivityTemplateIds.includes(idTemplate)) {
    return true
  }

  return (
    idTemplate.endsWith("_activity_log") ||
    idTemplate.startsWith("custom_activity_template_")
  )
}

// Determines the template context for a given form view based on its template ID and audit type.
function getFormViewTemplateContext({ idTemplate, templateAuditType }) {
  // Group templates intentionally bypass generic audit-type behavior.
  if (idTemplate === "group_session") {
    return "group_participant"
  }

  if (idTemplate === "group_supervision_session") {
    return "group_staff"
  }

  // Organization-audited templates include activity and non-activity templates.
  // Only activity templates should use the activity log sidebar.
  if (templateAuditType === "organization") {
    return isActivityTemplateId(idTemplate) ? "activity" : "organization"
  }

  if (templateAuditType === "staff") {
    return "staff"
  }

  if (templateAuditType === "participant") {
    return "participant"
  }

  // Unknown/empty audit type falls back to participant-style default handling.
  return "unknown"
}

// Resolves the appropriate sidebar content component for a given form view based on its template context and current participant/staff selection.
function resolveFormViewSidebarContent({
  idTemplate,
  templateAuditType,
  currentParticipantId,
  currentStaffMembershipId,
}) {
  const hasCurrentParticipant = hasValue(currentParticipantId)
  const hasCurrentStaff = hasValue(currentStaffMembershipId)
  const templateContext = getFormViewTemplateContext({
    idTemplate,
    templateAuditType,
  })

  // Group contexts always use group sidebars regardless of selected participant/staff.
  if (templateContext === "group_staff") {
    return <StaffGroupSidebar />
  }

  if (templateContext === "group_participant") {
    return <ParticipantGroupSidebar />
  }

  if (templateContext === "activity") {
    return <ActivityLogSidebar />
  }

  // Staff context prefers the selected staff sidebar and falls back to staff group context.
  if (templateContext === "staff") {
    return hasCurrentStaff ? <StaffSidebar /> : <StaffGroupSidebar />
  }

  // Participant context prefers the selected participant sidebar and falls back to participant group context.
  if (templateContext === "participant") {
    return hasCurrentParticipant ? (
      <ParticipantSidebar isFormViewActive={true} />
    ) : (
      <ParticipantGroupSidebar />
    )
  }

  // Default to participant context, with group sidebar when no participant is selected yet.
  return hasCurrentParticipant ? (
    <ParticipantSidebar isFormViewActive={true} />
  ) : (
    <ParticipantGroupSidebar />
  )
}

export { getFormViewTemplateContext, resolveFormViewSidebarContent }
